import React, { useMemo, useCallback } from 'react';
import {
     Accordion,
     AccordionSummary,
     AccordionDetails,
     Typography,
     Checkbox,
     Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { DivisionMileStone } from '../../../../../../api/divisionMileStones/types';
import { BaseChip } from './BaseChip';
import { TaskItem } from './TaskItem';
import { styles } from './MilestoneAccordion.styles';
import { MilestoneChipData } from './types';

interface MilestoneAccordionProps {
     milestone: {
          mileStoneCode: string;
          mileStoneName: string;
          mileStoneOrder: number;
          tasks: DivisionMileStone[];
     };
     index?: number; // For drag and drop
     isDragDisabled?: boolean;
     dragHandleProps?: any; // For drag handle
     isExpanded: boolean;
     onAccordionChange: (milestoneCode: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
     onMilestoneCheckboxChange: (milestoneCode: string, checked: boolean) => void;
     onTaskCheckboxChange: (taskId: string, checked: boolean) => void;
     getTaskCheckboxState: (taskId: string) => boolean;
     getDisplayTasks: (milestone: any) => DivisionMileStone[];
     getTaskChipType: (task: DivisionMileStone) => { label: string; color: 'default' | 'info' | 'success'; customColor?: string };
     getMilestoneChipData: (milestone: any) => MilestoneChipData | null;
     areAllTasksChecked: (milestone: any) => boolean;
     areSomeTasksChecked: (milestone: any) => boolean;
     // Status chip props for unsaved changes detection
     hasDraftChanges?: boolean;
     hasUnsavedChanges?: boolean;
     workflowTasksCount?: number;
     allTasksReleased?: boolean;
     hasMilestoneSyncProgress?: boolean; // New prop to check if this milestone has sync progress
     hasMilestoneUnsavedChanges?: boolean; // New prop to check if this milestone has unsaved changes
     // Drag and drop props
     onReorderTasks?: (milestoneCode: string, sourceIndex: number, destinationIndex: number) => void;
     divisionSettingsReordering?: boolean;
}

export const MilestoneAccordion: React.FC<MilestoneAccordionProps> = React.memo(({
     milestone,
     dragHandleProps,
     isExpanded,
     onAccordionChange,
     onMilestoneCheckboxChange,
     onTaskCheckboxChange,
     getTaskCheckboxState,
     getDisplayTasks,
     getTaskChipType,
     getMilestoneChipData,
     areAllTasksChecked,
     areSomeTasksChecked,
     hasDraftChanges,
     hasUnsavedChanges,
     workflowTasksCount,
     allTasksReleased,
     hasMilestoneSyncProgress,
     hasMilestoneUnsavedChanges,
     onReorderTasks,
     divisionSettingsReordering,
}) => {

     const displayTasks = useMemo(() => getDisplayTasks(milestone), [milestone, getDisplayTasks]);
     const hookAreAllTasksChecked = useMemo(() => areAllTasksChecked(milestone), [milestone, areAllTasksChecked]);
     const hookAreSomeTasksChecked = useMemo(() => areSomeTasksChecked(milestone), [milestone, areSomeTasksChecked]);

     const handleAccordionChange = useCallback((event: React.SyntheticEvent, expanded: boolean) => {
          onAccordionChange(milestone.mileStoneCode)(event, expanded);
     }, [milestone.mileStoneCode, onAccordionChange]);

     const handleMilestoneCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
          e.stopPropagation();
          onMilestoneCheckboxChange(milestone.mileStoneCode, e.target.checked);
     }, [milestone.mileStoneCode, onMilestoneCheckboxChange]);

     const handleMilestoneCheckboxClick = useCallback((e: React.MouseEvent) => {
          e.stopPropagation();
     }, []);

     const handleSummaryClick = useCallback((e: React.MouseEvent) => {
          if (!(e.target as HTMLElement).closest('input[type="checkbox"]')) {
               onAccordionChange(milestone.mileStoneCode)(e, !isExpanded);
          }
     }, [milestone.mileStoneCode, isExpanded, onAccordionChange]);

     const isTaskDragDisabled = useCallback((task: DivisionMileStone) => {
          const restrictedTaskNames = [
               'Track and confirm 5-day Application date/Proof of funds',
               'Loan Docs received'
          ].map(name => name.toLowerCase().trim());
          
          const currentTaskName = (task.taskName || '').toLowerCase().trim();
          return task.automated || restrictedTaskNames.includes(currentTaskName);
     }, []);

     return (
          <Accordion
               expanded={isExpanded}
               onChange={handleAccordionChange}
               sx={{
                    ...styles.container,
                    overflow: 'visible',
                    '& .MuiAccordionDetails-root': {
                         overflow: 'visible',
                    },
               }}
               slotProps={{
                    transition: {
                         unmountOnExit: true,
                    },
               }}
          >
               <AccordionSummary
                    sx={styles.summary}
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${milestone.mileStoneCode}-content`}
                    id={`${milestone.mileStoneCode}-header`}
               >
                    <Box sx={styles.contentContainer}>
                         <Box
                              sx={styles.summaryContent}
                              onClick={handleSummaryClick}
                         >
                              <Checkbox
                                   checked={hookAreAllTasksChecked}
                                   indeterminate={hookAreSomeTasksChecked && !hookAreAllTasksChecked}
                                   onChange={handleMilestoneCheckboxChange}
                                   onClick={handleMilestoneCheckboxClick}
                                   sx={styles.milestoneCheckbox}
                                   aria-label={`Select all tasks for ${milestone.mileStoneName}`}
                              />
                              <Typography sx={styles.titleText}>
                                   {milestone.mileStoneName}
                              </Typography>
                              <Typography sx={styles.taskCount}>
                                   {displayTasks.length} tasks
                              </Typography>
                         </Box>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BaseChip
                                   milestone={milestone}
                                   getMilestoneChipData={(milestone) => getMilestoneChipData(milestone)}
                                   areAllTasksChecked={hookAreAllTasksChecked}
                                   hasDraftChanges={hasDraftChanges}
                                   hasUnsavedChanges={hasUnsavedChanges}
                                   workflowTasksCount={workflowTasksCount}
                                   allTasksReleased={allTasksReleased}
                                   hasMilestoneSyncProgress={hasMilestoneSyncProgress}
                                   hasMilestoneUnsavedChanges={hasMilestoneUnsavedChanges}
                              />

                              {/* Drag handle for milestone reordering */}
                              {dragHandleProps && divisionSettingsReordering && (
                                   <Box
                                        {...dragHandleProps}
                                        sx={{
                                             cursor: 'grab',
                                             color: '#666',
                                             display: 'flex',
                                             alignItems: 'center',
                                             padding: 0.5,
                                             borderRadius: 1,
                                             '&:hover': {
                                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                             },
                                             '&:active': {
                                                  cursor: 'grabbing',
                                             },
                                        }}
                                   >
                                        <DragIndicatorIcon fontSize="small" />
                                   </Box>
                              )}

                              {/*showMilestoneActions && (
                                   <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Tooltip title="Add tasks to milestone" arrow>
                                             <IconButton
                                                  size="small"
                                                  onClick={handleAddMilestone}
                                                  sx={{
                                                       color: '#005DAA',
                                                       '&:hover': {
                                                            backgroundColor: 'rgba(0, 93, 170, 0.04)',
                                                       }
                                                  }}
                                             >
                                                  <AddIcon fontSize="small" />
                                             </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove milestone" arrow>
                                             <IconButton
                                                  size="small"
                                                  onClick={handleRemoveMilestone}
                                                  sx={{
                                                       color: '#f44336',
                                                       '&:hover': {
                                                            backgroundColor: 'rgba(244, 67, 54, 0.04)',
                                                       }
                                                  }}
                                             >
                                                  <DeleteIcon fontSize="small" />
                                             </IconButton>
                                        </Tooltip>
                                   </Box>
                              )*/}
                         </Box>
                    </Box>
               </AccordionSummary>

               <AccordionDetails
                    sx={styles.details}
                    id={`${milestone.mileStoneCode}-content`}
               >
                    {onReorderTasks ? (
                         <Droppable
                              droppableId={milestone.mileStoneCode}
                              type="task"
                         >
                              {(provided, snapshot) => (
                                   <Box
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        sx={{
                                             backgroundColor: snapshot.isDraggingOver ? '#e8f5e8' : 'transparent',
                                             border: snapshot.isDraggingOver ? '2px solid #4caf50' : 'none',
                                             borderRadius: 1,
                                             // Use consistent padding to prevent shaking during drag
                                             padding: snapshot.isDraggingOver ? 1 : 0,
                                             // Disable transitions during drag to prevent shaking
                                             transition: snapshot.isDraggingOver ? 'none' : 'background-color 0.2s ease, border 0.2s ease',
                                             minHeight: '20px',
                                             position: 'relative',
                                        }}
                                   >
                                        {displayTasks.map((task, taskIndex) => (
                                             <Draggable
                                                  key={task.id}
                                                  draggableId={task.id}
                                                  index={taskIndex}
                                                  isDragDisabled={isTaskDragDisabled(task)}
                                             >
                                                  {(provided, snapshot) => (
                                                       <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            style={{
                                                                 ...provided.draggableProps.style,
                                                                 zIndex: snapshot.isDragging ? '10000' : undefined,
                                                            }}
                                                       >
                                                            <TaskItem
                                                                 task={task}
                                                                 getTaskCheckboxState={getTaskCheckboxState}
                                                                 onTaskCheckboxChange={onTaskCheckboxChange}
                                                                 getTaskChipType={getTaskChipType}
                                                                 dragHandleProps={provided.dragHandleProps}
                                                                 isDragging={snapshot.isDragging}
                                                                 divisionSettingsReordering={divisionSettingsReordering}
                                                            />
                                                       </div>
                                                  )}
                                             </Draggable>
                                        ))}
                                        {provided.placeholder}
                                   </Box>
                              )}
                         </Droppable>
                    ) : (
                         <div>
                              {displayTasks.map((task) => (
                                   <TaskItem
                                        key={task.id}
                                        task={task}
                                        getTaskCheckboxState={getTaskCheckboxState}
                                        onTaskCheckboxChange={onTaskCheckboxChange}
                                        getTaskChipType={getTaskChipType}
                                        divisionSettingsReordering={divisionSettingsReordering}
                                   />
                              ))}
                         </div>
                    )}
               </AccordionDetails>
          </Accordion>
     );
});